import { useEffect, useId } from 'react';

import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// JobETAChannel never broadcasts: the backend only counts its subscribers to decide whether a
// moving truck is worth re-routing. A refreshed ETA arrives over CompanyJobUpdateChannel.
const noop = () => {};

/**
 * Tells the backend a dispatcher is watching this job's ETA. Each watched job costs a routing
 * call per refresh period while its driver moves, so `enabled` should cover only a job being
 * re-predicted — an en-route leg. A dwell count-up is clock-driven and needs no server.
 */
export default function useJobEtaWatch(jobId: string, enabled: boolean) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const uniqueKey = useId();

  useEffect(() => {
    if (!enabled) return;

    const createOptions = { job_id: jobId };

    subscribeWithMultipleHandlers(
      realTimeChannels.JobETAChannel,
      companyId,
      uniqueKey,
      noop,
      createOptions,
    );

    return () => {
      unsubscribeHandler(
        realTimeChannels.JobETAChannel,
        companyId,
        uniqueKey,
        createOptions,
      );
    };
  }, [companyId, enabled, jobId, uniqueKey]);
}
